// inv/src/pages/payment/PurchaseMembershipBlockPricingPage.jsx
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PaymentProcess from './PaymentProcess';
import { useSubscriptionLogic } from './subscriptionLogic';
import PlanCard, { getPlanConfigs } from './PlanCard';
import { AlertBox } from '../../components/ui/alertBox';
import { Dialog } from '../../components/ui/dialog';
import { CircleCheckBig } from 'lucide-react';
import { useMembershipAccess } from '../../hooks/membership';

export default function PurchaseMembershipBlockPricingPage() {
  const { t, i18n } = useTranslation();
  const {
    loading,
    user,
    plans,
    selectedInterval,
    setSelectedInterval,
    checkoutPlanId,
    setCheckoutPlanId,
    handlePlanSelect,
    fetchUser,
    previewUpgrade,
    isUpdating,
    clearIncompleteCheckout,
    checkSubscriptionStatus,
  } = useSubscriptionLogic();

  const navigate = useNavigate();
  const [lastPreview, setLastPreview] = useState(null);

  const status = user?.status ?? null;
  const endsAt = user?.endsAt ?? null;
  const isAuthenticated = Boolean(status);

  useEffect(() => {
    if (isAuthenticated && user?.incompleteCheckout?.planId) {
      const checkoutTime = new Date(user.incompleteCheckout.createdAt);
      const now = new Date();
      const hoursDiff = (now - checkoutTime) / (1000 * 60 * 60);

      if (hoursDiff > 24) {
        clearIncompleteCheckout();
      }
    }
  }, [isAuthenticated, user?.incompleteCheckout, clearIncompleteCheckout]);

  useEffect(() => {
    if (
      isAuthenticated &&
      user?.membershipPeriod &&
      selectedInterval !== user.membershipPeriod
    ) {
      setSelectedInterval(user.membershipPeriod);
    }
  }, [isAuthenticated, user?.membershipPeriod]);

  const redirectToTY = ({
    planName = 'your plan',
    immediate = null,
    recurring = null,
    credit = null,
    currency = 'USD',
    nextBilledAt = null,
    downgrade = false,
  }) => {
    const payload = {
      planName,
      immediate,
      recurring,
      credit,
      currency,
      nextBilledAt,
      downgrade,
    };

    sessionStorage.setItem('ty-payload', JSON.stringify(payload));

    navigate('/thank-you', {
      replace: true,
      state: payload,
    });
  };

  const [showFreeConfirm, setShowFreeConfirm] = useState(false);
  const [queuedFree, setQueuedFree] = useState(false);
  const [prorationDialog, setProrationDialog] = useState(null);
  const [incompleteInfo, setIncompleteInfo] = useState(null);
  const [planActionError, setPlanActionError] = useState(null);
  const membership = useMembershipAccess({ user, loading });

  if (loading) return <div>Loading…</div>;

  const planConfigs = getPlanConfigs('pricing', plans, selectedInterval, t);

  const onSelect = (config) => {
    if (isUpdating) return;
    setPlanActionError(null);

    const samePlan = config.planId === user?.lastPriceId;
    const samePeriod = selectedInterval === user?.membershipPeriod;
    if (samePlan && samePeriod) return;

    if (!isAuthenticated) {
      window.location.href =
        i18n.language === 'en' ? '/signup?utm_source=crino-pricing-page&utm_medium=signup-button' : `/${i18n.language}/signup?utm_source=crino-pricing-page&utm_medium=signup-button`;
      return;
    }

    if (config.key === 'free') {
      setShowFreeConfirm(true);
      return;
    }

    if (!config.planId) return;

    if (membership.canPreviewPlanChange) {
      (async () => {
        const preview = await previewUpgrade({
          priceId: config.planId,
          prorationMode: 'prorated_immediately',
        });

        if (!preview) {
          setPlanActionError(t('pagePricing.content.other.planActionError'));
          return;
        }

        setLastPreview({ ...preview, planName: config.title });
        setProrationDialog({
          newPlanId: config.planId,
          newPlanName: config.title,
          ...preview,
        });
      })();
      return;
    }

    setLastPreview({ planName: config.title });
    (async () => {
      const updatedUser = await handlePlanSelect({
        planId: config.planId,
        prorationMode: 'prorated_immediately',
      });

      if (!updatedUser) {
        setPlanActionError(t('pagePricing.content.other.planActionError'));
      }
    })();
  };

  return (
    <div className="max-w-6xl mx-auto font-sans px-12 md:px-12 lg:px-24">
      {!isAuthenticated && (
        <div className="flex justify-center mt-4">
          <AlertBox variant="yellow">
            <p className="text-sm">
              {t('pagePricing.content.signupText')}{' '}
              <a
                href={i18n.language === 'en' ? '/signup' : `/${i18n.language}/signup`}
                className="font-bold underline"
              >
                {t('pagePricing.content.signupButton')}
              </a>
            </p>
          </AlertBox>
        </div>
      )}

      {isAuthenticated && status === 'past_due' && (
        <div className="flex justify-center mt-4">
          <AlertBox variant="red">
            {t('pagePricing.content.other.pastDueWarning')}
          </AlertBox>
        </div>
      )}

      {isAuthenticated && status === 'past_due' && (
        <div className="flex justify-center mt-4">
          <AlertBox variant="red">
            {t('pagePricing.content.other.paymentFailedWarning')}
          </AlertBox>
        </div>
      )}

      {isAuthenticated && status === 'cancelled' && endsAt && (
        <div className="flex justify-center mt-4">
          <AlertBox variant="yellow">
            {t(
              'pagePricing.content.other.fCancelled',
              'Your subscription has been cancelled and will end on {{date}}.',
              { date: new Date(endsAt).toLocaleDateString(i18n.language) }
            )}
          </AlertBox>
        </div>
      )}

      {planActionError && (
        <div className="flex justify-center mt-4">
          <AlertBox variant="red">{planActionError}</AlertBox>
        </div>
      )}

      <div className="flex mx-auto bg-custom1 rounded-full max-w-xs p-1.5 mt-8">
        {['month', 'quarter', 'year'].map((period) => (
          <button
            key={period}
            className={`cursor-pointer flex-1 text-stone-950 text-sm py-2 px-4 rounded-full ${selectedInterval === period ? 'bg-custom3 text-white' : ''
              }`}
            onClick={() => !isUpdating && setSelectedInterval(period)}
            disabled={isUpdating}
          >
            {t(`pagePricing.content.other.f${period === 'month' ? '1' : period === 'quarter' ? '2' : '3'}`)}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-12 items-stretch">
        {planConfigs.map((config) => {
          const isCurrent =
            isAuthenticated && membership.isCurrentPlan(config.key, selectedInterval);

          const isPending =
            isAuthenticated && membership.isPendingPlan(config.key, selectedInterval);

          const isIncomplete = isAuthenticated && (
            user.incompleteCheckout?.planId === config.planId &&
            user.incompleteCheckout?.interval === selectedInterval
          );

          return (
            <div key={config.key} className="relative h-full">
              <PlanCard
                planKey={config.key}
                title={config.title}
                price={config.price}
                description={config.description}
                backgroundClass={config.backgroundClass}
                textClasses={config.textClasses}
                features={config.features}
                periodLabel={config.periodLabel}
                disabled={isCurrent || queuedFree || isPending || isUpdating}
                pending={isPending}
                buttonClass={config.buttonClass}
                config={config}
                onPlanSelect={onSelect}
                isAuthenticated={isAuthenticated}
              />

              {isIncomplete && (
                <button
                  type="button"
                  onClick={() => setIncompleteInfo({ planName: config.title, planId: config.planId })}
                  className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow ring-1 ring-black/10 p-1"
                  aria-label={t('pagePricing.content.other.incompleteCheckout', 'You have an incomplete checkout.')}
                  title={t('pagePricing.content.other.incompleteCheckout', 'You have an incomplete checkout.')}
                >
                  <CircleCheckBig className="cursor-pointer h-5 w-5 animate-bounce" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {checkoutPlanId && (
        <PaymentProcess
          priceId={checkoutPlanId}
          userId={user.user_id}
          paddleSubscriptionId={user.paddleSubscriptionId}
          interval={selectedInterval}
          userStatus={user.status}
          pendingMembershipType={user.pendingMembershipType}
          planName={
            (planConfigs.find(p => p.planId === checkoutPlanId)?.title)
            || (lastPreview?.planName)
            || 'your plan'
          }
          onClose={() => setCheckoutPlanId(null)}
          onSuccess={async () => {
            const priceId = checkoutPlanId;
            setCheckoutPlanId(null);
            await fetchUser();
            const fallbackName =
              planConfigs.find(p => p.planId === priceId)?.title || 'your plan';
            redirectToTY(lastPreview ?? { planName: fallbackName });
          }}
        />
      )}

      {showFreeConfirm && (
        <Dialog
          isOpen
          title={t('pagePricing.content.other.f14', 'Confirm downgrade')}
          subtitle={t('pagePricing.content.other.cancelImmediatelyWarning', 'Your subscription will be cancelled immediately and you will lose access to all paid features. No refunds will be provided. If you want to keep your subscription until the end of the billing period, please cancel through the customer portal instead.')}

          buttonLabel={t('pagePricing.content.other.f16', 'Yes, downgrade to Free')}
          onButtonClick={async () => {
            setShowFreeConfirm(false);
            setQueuedFree(true);

            const updatedUser = await handlePlanSelect({ planId: null });

            redirectToTY({
              planName: t('pagePricing.content.freeMembership.name'),
              immediate: null,
              credit: 0,
              currency: 'USD',
              nextBilledAt: updatedUser?.endsAt ?? null,
              downgrade: true,
            });

            setQueuedFree(false);
          }}

          onClose={() => setShowFreeConfirm(false)}
        />
      )}

      {prorationDialog && (
        <Dialog
          isOpen
          title={t('pagePricing.content.other.changePlan', 'Change plan')}
          subtitle={
            <div>
              <p className="mb-2">
                {(() => {
                  const {
                    immediate = 0,
                    recurring = 0,
                    credit = 0,
                    balanceCredit = 0,
                    currency = 'USD',
                    nextBilledAt,
                  } = prorationDialog || {};

                  const periodMap = {
                    month: t('pagePricing.content.other.f1.1'),
                    quarter: t('pagePricing.content.other.f2'),
                    year: t('pagePricing.content.other.f3.1'),
                  };
                  const period = periodMap[selectedInterval];

                  if (immediate > 0 && credit > 0) {
                    return t('pagePricing.content.other.prorationNowWithCredit', {
                      credit: credit.toFixed(2),
                      immediate: immediate.toFixed(2),
                      recurring: recurring.toFixed(2),
                      currency,
                      period,
                      date: nextBilledAt
                        ? new Date(nextBilledAt).toLocaleDateString(
                          i18n.language,
                          { year: 'numeric', month: 'long', day: 'numeric' }
                        )
                        : t('pagePricing.content.other.nextCycleShort', 'next cycle'),
                    });
                  }

                  if (immediate === 0 && balanceCredit > 0) {
                    return t('pagePricing.content.other.prorationBalance', {
                      credit: balanceCredit.toFixed(2),
                      recurring: recurring.toFixed(2),
                      currency,
                      period,
                    });
                  }

                  if (immediate > 0) {
                    return t('pagePricing.content.other.prorationNow', {
                      immediate: immediate.toFixed(2),
                      recurring: recurring.toFixed(2),
                      currency,
                      period,
                      date: nextBilledAt
                        ? new Date(nextBilledAt).toLocaleDateString(
                          i18n.language,
                          { year: 'numeric', month: 'long', day: 'numeric' }
                        )
                        : t('pagePricing.content.other.nextCycleShort', 'next cycle'),
                    });
                  }

                  if (immediate < 0) {
                    return t('pagePricing.content.other.prorationCredit', {
                      credit: Math.abs(immediate).toFixed(2),
                      recurring: recurring.toFixed(2),
                      currency,
                      period,
                    });
                  }

                  return t('pagePricing.content.other.prorationZero', {
                    recurring: recurring.toFixed(2),
                    currency,
                    period,
                  });
                })()}
              </p>
            </div>
          }


          buttonLabel={t('pagePricing.content.other.payNow', 'Pay now')}
          secondaryButtonLabel={t('pagePricing.content.other.nextCycle', 'Next billing date')}
          onButtonClick={async () => {
            setPlanActionError(null);
            const updatedUser = await handlePlanSelect({
              planId: prorationDialog.newPlanId,
              prorationMode: 'prorated_immediately',
            });
            setProrationDialog(null);
            if (updatedUser) {
              redirectToTY(
                lastPreview ?? { planName: prorationDialog.newPlanName }
              );
            } else {
              setPlanActionError(t('pagePricing.content.other.planActionError'));
            }
          }}
          onSecondaryClick={async () => {
            setPlanActionError(null);
            const updatedUser = await handlePlanSelect({
              planId: prorationDialog.newPlanId,
              prorationMode: 'prorated_next_billing_period',
            });
            setProrationDialog(null);
            if (updatedUser) {
              redirectToTY(
                lastPreview ?? { planName: prorationDialog.newPlanName }
              );
            } else {
              setPlanActionError(t('pagePricing.content.other.planActionError'));
            }
          }}
          onClose={() => setProrationDialog(null)}
        />
      )}
      {incompleteInfo && (
        <Dialog
          isOpen
          title={t('pagePricing.content.other.incompleteCheckoutTitle', 'Incomplete checkout')}
          subtitle={
            <div className="space-y-2">
              <p>
                {t(
                  'pagePricing.content.other.incompleteCheckoutForPlan',
                  'You have an incomplete checkout for the "{{planName}}" plan. You can continue the payment at any time.',
                  { planName: incompleteInfo.planName }
                )}
              </p>
            </div>
          }
          buttonLabel={t('pagePricing.content.other.continue', 'Continue')}
          onButtonClick={() => setIncompleteInfo(null)}
          onClose={() => setIncompleteInfo(null)}
        />
      )}
    </div>
  );
}
